/**
 * PolicyRichEditor
 *
 * Word-like WYSIWYG editor built on TipTap. Renders HTML content coming from
 * a mammoth-converted `.docx` upload (or existing saved policy HTML) and
 * lets admins edit headings, paragraphs, bold/italic/underline, alignment,
 * bullet + numbered lists, tables, hyperlinks and images inline.
 *
 * Emits HTML via `onChange` — persist that into `policy.content_html`.
 */
import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { TextAlign } from '@tiptap/extension-text-align';
import {
  Bold, Italic, Underline as UIcon, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Link as LinkIcon,
  Table as TableIcon, Heading1, Heading2, Heading3, Undo2, Redo2, Image as ImageIcon,
} from 'lucide-react';

function TbBtn({ onClick, active, disabled, title, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`p-1.5 rounded hover:bg-slate-200 disabled:opacity-40 ${active ? 'bg-slate-300 text-slate-900' : 'text-slate-700'}`}
    >
      {children}
    </button>
  );
}

export default function PolicyRichEditor({ html, onChange, editable = true, testid = 'policy-rich-editor' }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-blue-700 underline' } }),
      Image.configure({ HTMLAttributes: { class: 'max-w-full h-auto' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: true, HTMLAttributes: { class: 'policy-table' } }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: html || '',
    editable,
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
  });

  // Sync incoming HTML when parent replaces it (e.g., after Word upload).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((html || '') !== current) {
      editor.commands.setContent(html || '', false);
    }
  }, [html, editor]);

  if (!editor) return null;
  const isImage = () => editor.isActive('image');

  return (
    <div className="border border-slate-300 rounded-sm bg-white" data-testid={testid}>
      {editable && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-1.5 py-1 text-sm">
          <TbBtn onClick={() => editor.chain().focus().undo().run()} title="Undo" testid="editor-undo"><Undo2 className="w-4 h-4" /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().redo().run()} title="Redo" testid="editor-redo"><Redo2 className="w-4 h-4" /></TbBtn>
          <span className="w-px h-5 bg-slate-300 mx-1" />
          <TbBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1"><Heading1 className="w-4 h-4" /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2"><Heading2 className="w-4 h-4" /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3"><Heading3 className="w-4 h-4" /></TbBtn>
          <span className="w-px h-5 bg-slate-300 mx-1" />
          <TbBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><Bold className="w-4 h-4" /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><Italic className="w-4 h-4" /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline"><UIcon className="w-4 h-4" /></TbBtn>
          <span className="w-px h-5 bg-slate-300 mx-1" />
          <TbBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left"><AlignLeft className="w-4 h-4" /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align center"><AlignCenter className="w-4 h-4" /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right"><AlignRight className="w-4 h-4" /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justify"><AlignJustify className="w-4 h-4" /></TbBtn>
          <span className="w-px h-5 bg-slate-300 mx-1" />
          <TbBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list"><List className="w-4 h-4" /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list"><ListOrdered className="w-4 h-4" /></TbBtn>
          <span className="w-px h-5 bg-slate-300 mx-1" />
          <TbBtn onClick={() => {
            const url = window.prompt('Link URL', editor.getAttributes('link').href || 'https://');
            if (url === null) return;
            if (url === '') editor.chain().focus().unsetLink().run();
            else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
          }} active={editor.isActive('link')} title="Link"><LinkIcon className="w-4 h-4" /></TbBtn>
          <TbBtn onClick={() => {
            const src = window.prompt('Image URL (or paste a data URL)');
            if (src) editor.chain().focus().setImage({ src }).run();
          }} title="Image" active={isImage()}><ImageIcon className="w-4 h-4" /></TbBtn>
          <span className="w-px h-5 bg-slate-300 mx-1" />
          <TbBtn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table"><TableIcon className="w-4 h-4" /></TbBtn>
          <TbBtn onClick={() => editor.chain().focus().addRowAfter().run()} title="Add row" disabled={!editor.isActive('table')}>+ Row</TbBtn>
          <TbBtn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add column" disabled={!editor.isActive('table')}>+ Col</TbBtn>
          <TbBtn onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table" disabled={!editor.isActive('table')}>× Tbl</TbBtn>
        </div>
      )}
      <div className="policy-editor-shell">
        <div className="policy-editor-page">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
}
